/**
 * Centralized Template Client
 * Shared client for accessing templates from the brand microservice
 * Used by both user-webapp and template-editor
 */

class CentralizedTemplateClient {
  constructor(config = {}) {
    this.baseUrl =
      config.baseUrl ||
      process.env.REACT_APP_BRAND_MS ||
      process.env.VITE_APP_BRAND_MS ||
      "http://localhost:3003";
    this.clientSource = config.clientSource || "unknown";
    this.cache = new Map();
    this.cacheTimeout = config.cacheTimeout || 5 * 60 * 1000; // 5 minutes
    this.authToken = config.authToken || null;
  }

  /**
   * Get headers for requests
   */
  getHeaders() {
    const headers = {
      "Content-Type": "application/json",
      "X-Client-Source": this.clientSource
    };

    if (this.authToken) {
      headers["Authorization"] = `Bearer ${this.authToken}`;
    }

    return headers;
  }

  /**
   * Set authentication token
   */
  setAuthToken(token) {
    this.authToken = token;
  }

  /**
   * Fetch with error handling
   */
  async fetchWithRetry(url, options = {}, retries = 3) {
    for (let i = 0; i < retries; i++) {
      try {
        const response = await fetch(url, {
          ...options,
          headers: {
            ...this.getHeaders(),
            ...options.headers
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Normalize common API response envelopes
        if (data?.data) {
          return data.data;
        }

        if (data?.responseData !== undefined) {
          return data.responseData;
        }

        if (data?.result) {
          return data.result;
        }

        return data;
      } catch (error) {
        console.warn(`Fetch attempt ${i + 1} failed:`, error);

        if (i === retries - 1) {
          throw error;
        }

        // Wait before retry with exponential backoff
        await new Promise((resolve) =>
          setTimeout(resolve, Math.pow(2, i) * 1000)
        );
      }
    }
  }

  /**
   * Get all templates
   */
  async getAllTemplates(includeInactive = false) {
    const cacheKey = `templates_all_${includeInactive}`;

    // Check cache
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        console.log("📦 Using cached templates");
        return cached.data;
      }
    }

    try {
      const url = `${this.baseUrl}/api/templates${includeInactive ? "?includeInactive=true" : ""}`;
      const templates = await this.fetchWithRetry(url);

      // Update cache
      this.cache.set(cacheKey, {
        data: templates,
        timestamp: Date.now()
      });

      console.log(
        `✅ Fetched ${templates.length} templates from central service`
      );
      return templates;
    } catch (error) {
      console.error("Failed to fetch templates from central service:", error);

      // Return cached data if available (even if expired)
      if (this.cache.has(cacheKey)) {
        console.log("⚠️ Using expired cache due to fetch failure");
        return this.cache.get(cacheKey).data;
      }

      // Return empty array as last resort
      return [];
    }
  }

  /**
   * Get template by ID
   */
  async getTemplateById(id) {
    const cacheKey = `template_${id}`;

    // Check cache
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        console.log(`📦 Using cached template: ${id}`);
        return cached.data;
      }
    }

    try {
      const url = `${this.baseUrl}/api/templates/${id}`;
      const template = await this.fetchWithRetry(url);

      // Update cache
      this.cache.set(cacheKey, {
        data: template,
        timestamp: Date.now()
      });

      console.log(`✅ Fetched template ${id} from central service`);
      return template;
    } catch (error) {
      console.error(`Failed to fetch template ${id}:`, error);

      // Return cached data if available
      if (this.cache.has(cacheKey)) {
        return this.cache.get(cacheKey).data;
      }

      return null;
    }
  }

  /**
   * Get templates by category
   */
  async getTemplatesByCategory(category) {
    const cacheKey = `templates_category_${category}`;

    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.data;
      }
    }

    try {
      const url = `${this.baseUrl}/api/templates/category/${category}`;
      const templates = await this.fetchWithRetry(url);

      this.cache.set(cacheKey, {
        data: templates,
        timestamp: Date.now()
      });

      return templates;
    } catch (error) {
      console.error(
        `Failed to fetch templates for category ${category}:`,
        error
      );
      return [];
    }
  }

  /**
   * Get templates by type
   */
  async getTemplatesByType(type) {
    const cacheKey = `templates_type_${type}`;

    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.data;
      }
    }

    try {
      const url = `${this.baseUrl}/api/templates/type/${type}`;
      const templates = await this.fetchWithRetry(url);

      this.cache.set(cacheKey, {
        data: templates,
        timestamp: Date.now()
      });

      return templates;
    } catch (error) {
      console.error(`Failed to fetch templates for type ${type}:`, error);
      return [];
    }
  }

  /**
   * Search templates
   */
  async searchTemplates(query, filters = {}) {
    try {
      const params = new URLSearchParams({ q: query, ...filters });
      const url = `${this.baseUrl}/api/templates/search?${params}`;
      return await this.fetchWithRetry(url);
    } catch (error) {
      console.error("Template search failed:", error);
      return [];
    }
  }

  /**
   * Get template constraints
   */
  async getTemplateConstraints(id) {
    const cacheKey = `constraints_${id}`;

    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.data;
      }
    }

    try {
      const url = `${this.baseUrl}/api/templates/${id}/constraints`;
      const constraints = await this.fetchWithRetry(url);

      this.cache.set(cacheKey, {
        data: constraints,
        timestamp: Date.now()
      });

      return constraints;
    } catch (error) {
      console.error(`Failed to fetch constraints for template ${id}:`, error);
      return null;
    }
  }

  /**
   * Create a new template
   */
  async createTemplate(templateData) {
    try {
      const url = `${this.baseUrl}/api/templates`;
      const response = await this.fetchWithRetry(url, {
        method: "POST",
        body: JSON.stringify(templateData)
      });

      // Clear cache
      this.clearCache();

      console.log("✅ Template created successfully");
      return response;
    } catch (error) {
      console.error("Failed to create template:", error);
      throw error;
    }
  }

  /**
   * Update a template
   */
  async updateTemplate(id, updateData) {
    try {
      const url = `${this.baseUrl}/api/templates/${id}`;
      const response = await this.fetchWithRetry(url, {
        method: "PUT",
        body: JSON.stringify(updateData)
      });

      // Clear cache for this template
      this.cache.delete(`template_${id}`);
      this.cache.delete(`constraints_${id}`);

      console.log(`✅ Template ${id} updated successfully`);
      return response;
    } catch (error) {
      console.error(`Failed to update template ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a template
   */
  async deleteTemplate(id) {
    try {
      const url = `${this.baseUrl}/api/templates/${id}`;
      await this.fetchWithRetry(url, {
        method: "DELETE"
      });

      // Clear cache
      this.clearCache();

      console.log(`✅ Template ${id} deleted successfully`);
      return true;
    } catch (error) {
      console.error(`Failed to delete template ${id}:`, error);
      throw error;
    }
  }

  /**
   * Sync template from current application
   */
  async syncTemplate(templateData) {
    try {
      const url = `${this.baseUrl}/api/templates/sync`;
      const response = await this.fetchWithRetry(url, {
        method: "POST",
        body: JSON.stringify(templateData)
      });

      // Update cache for this template
      this.cache.set(`template_${templateData.id}`, {
        data: response,
        timestamp: Date.now()
      });

      console.log(`✅ Template ${templateData.id} synced successfully`);
      return response;
    } catch (error) {
      console.error("Failed to sync template:", error);
      throw error;
    }
  }

  /**
   * Clone a template
   */
  async cloneTemplate(id, name) {
    try {
      const url = `${this.baseUrl}/api/templates/${id}/clone`;
      const response = await this.fetchWithRetry(url, {
        method: "POST",
        body: JSON.stringify({ name })
      });

      // Clear cache
      this.clearCache();

      console.log(`✅ Template ${id} cloned successfully`);
      return response;
    } catch (error) {
      console.error(`Failed to clone template ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get template statistics
   */
  async getTemplateStats() {
    try {
      const url = `${this.baseUrl}/api/templates/stats`;
      return await this.fetchWithRetry(url);
    } catch (error) {
      console.error("Failed to fetch template stats:", error);
      return null;
    }
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
    console.log("🧹 Template cache cleared");
  }

  /**
   * Get cache status
   */
  getCacheStatus() {
    const items = Array.from(this.cache.entries()).map(([key, value]) => ({
      key,
      age: Date.now() - value.timestamp,
      expired: Date.now() - value.timestamp > this.cacheTimeout
    }));

    return {
      size: this.cache.size,
      items
    };
  }

  /**
   * Check if template exists locally (in cache)
   */
  hasTemplateInCache(id) {
    const cacheKey = `template_${id}`;
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      return Date.now() - cached.timestamp < this.cacheTimeout;
    }
    return false;
  }

  /**
   * Prefetch templates for performance
   */
  async prefetchTemplates(templateIds) {
    const promises = templateIds.map((id) => this.getTemplateById(id));
    await Promise.allSettled(promises);
    console.log(`📦 Prefetched ${templateIds.length} templates`);
  }
}

// ES6 default export
export default CentralizedTemplateClient;
